<template>
  <div class="admin">
    <nav class="admin-bar" role="navigation" aria-label="main navigation">
      <h1 class="admin-bar__title" :style="titleStyle">{{ info.name }}</h1>
      <div class="admin-bar__actions">
        <template v-if="isAdmin">
          <button type="button" class="button" @click="settingsModal = true">Settings</button>
          <button type="button" class="button" @click="addAgentModal = true">Add Agent</button>
          <button type="button" class="button" @click="listAgentModal = true">Agent List</button>
          <button type="button" class="button" @click="scheduleModal = true">Schedule Game</button>
        </template>
        <button type="button" class="button is-primary" @click="logout()">Logout</button>
      </div>
    </nav>

    <div class="admin-grid" :class="{ 'admin-grid--split': isAdmin }">
      <div class="tb-stack">
        <GameSummary
          v-if="summaryGame"
          :game="summaryGame.data"
          :game-id="summaryGame.id"
          :sold="soldCount"
          :my-sold="soldByAgent($store.state.user.email)"
          :claims="gameClaims"
          :sheet-count="sheetCount"
          :polled-count="liveData.length"
        />
        <p v-else class="tb-empty">No scheduled or running game. Create one from “Schedule Game”.</p>

        <!-- Live board while a game runs, otherwise the ticket assignment desk. -->
        <section v-if="showLiveBoard" class="tb-card">
          <header class="tb-card__head">
            <p class="tb-card__title">Numbers polled</p>
            <span class="tb-pill tb-pill--live"><span class="tb-pill__dot"></span>Live</span>
            <span class="tb-pill">{{ liveData.length }} of 90</span>
          </header>
          <div class="tb-card__body">
            <NumberBoard :called="liveData" :latest="lastPolled" />
          </div>
        </section>

        <section v-else class="tb-card">
          <header class="tb-card__head">
            <p class="tb-card__title">Assign a ticket</p>
            <span class="tb-pill">{{ ticketCount }} sold</span>
          </header>
          <div class="tb-card__body assign">
            <b-field label="Ticket" type="is-success" :message="ticketType">
              <b-input v-model.lazy="form.ticket" type="text" placeholder="e.g. 12 or 12,13,14" required></b-input>
            </b-field>
            <b-field label="Name">
              <b-input v-model.lazy="form.name" type="text" placeholder="Player name" required></b-input>
            </b-field>
            <b-field label="Phone">
              <b-input v-model.lazy="form.phone" type="tel" placeholder="Player phone (optional)"></b-input>
            </b-field>
            <div class="assign__actions">
              <button type="button" class="button is-primary" @click="assignTicket()">ASSIGN TICKET</button>
              <button type="button" class="button is-danger" @click="unAssignTicket()">UNASSIGN TICKET</button>
            </div>
          </div>
        </section>
      </div>

      <section v-if="isAdmin" class="tb-card">
        <header class="tb-card__head">
          <p class="tb-card__title">Games</p>
          <span class="tb-pill">{{ games.length }}</span>
        </header>
        <div class="tb-card__body admin-scroll">
          <button v-for="game in games" :key="game.id" type="button" class="admin-row" @click="gameAction(game)">
            <span class="admin-row__main">{{ formatSchedule(game.data().schedule.toDate()) }}</span>
            <span class="tag" :class="statusTag(game.data().status)">{{ game.data().status }}</span>
          </button>
          <p v-if="!games.length" class="tb-empty">No games yet.</p>
        </div>
      </section>
    </div>

    <div v-if="activeGame" class="admin-grid">
      <div class="tb-card">
        <b-tabs position="is-centered" class="block">
          <b-tab-item label="Ticket map">
            <div class="admin-legend">
              <span><i class="admin-legend__swatch admin-legend__swatch--free"></i>Available ({{ availableCount }})</span>
              <span><i class="admin-legend__swatch admin-legend__swatch--sold"></i>Sold ({{ soldCount }})</span>
              <span><i class="admin-legend__swatch admin-legend__swatch--picked"></i>Selected ({{ selectedTickets.length }})</span>
            </div>
            <div class="admin-map-wrap">
              <div class="admin-map">
                <button
                  v-for="slot in ticketSlots"
                  :key="slot.number"
                  type="button"
                  class="admin-map__cell"
                  :class="{ 'is-empty': !slot.card, 'is-sold': slot.sold, 'is-selected': slot.selected }"
                  :title="slot.sold ? `#${slot.number} — ${slot.card.name}` : `#${slot.number} — available`"
                  @click="showTicketInfo(slot.card)"
                >
                  {{ slot.card ? slot.number : "" }}
                </button>
              </div>
            </div>
            <p class="admin-hint">Tap a free ticket to add it to the assignment form, or a sold one to see who holds it.</p>
          </b-tab-item>

          <b-tab-item label="Agent summary">
            <button v-for="agent in agents" :key="agent.id" type="button" class="admin-row" @click="showAgentInfo(agent)">
              <span class="admin-row__main">
                {{ agent.data().name }}
                <small>({{ agent.data().email }})</small>
              </span>
              <span class="tb-pill">{{ soldByAgent(agent.data().email) }} sold</span>
            </button>
            <p v-if="!agents.length" class="tb-empty">No agents yet.</p>
          </b-tab-item>
        </b-tabs>
      </div>
    </div>

    <b-modal :active.sync="scheduleModal" has-modal-card full-screen trap-focus :destroy-on-hide="false" aria-role="dialog" aria-modal>
      <ScheduleAsk @ScheduleGame="createGame"></ScheduleAsk>
    </b-modal>
    <b-modal :active.sync="addAgentModal" has-modal-card trap-focus :destroy-on-hide="false" aria-role="dialog" aria-modal>
      <CreateAgent @AddAgent="addAgent"></CreateAgent>
    </b-modal>
    <b-modal :active.sync="listAgentModal" has-modal-card trap-focus :destroy-on-hide="false" aria-role="dialog" aria-modal>
      <AgentList ref="agentList" :del="isAdmin" @DelAgent="deleteAgent"></AgentList>
    </b-modal>
    <b-modal :active.sync="agentDetailModal" has-modal-card full-screen trap-focus :destroy-on-hide="false" aria-role="dialog" aria-modal>
      <AgentInfo :agent="selectedAgent"></AgentInfo>
    </b-modal>
    <b-modal :active.sync="gameInfoModal" has-modal-card full-screen trap-focus :destroy-on-hide="false" aria-role="dialog" aria-modal>
      <GameInfo ref="selectedGame" @EditGame="editGame" @stopGame="confirmCancelGame"></GameInfo>
    </b-modal>
    <b-modal :active.sync="settingsModal" has-modal-card full-screen trap-focus :destroy-on-hide="false" aria-role="dialog" aria-modal>
      <Settings @ChangeSettings="changeSettings"></Settings>
    </b-modal>
  </div>
</template>

<script>
  import { mapState } from "vuex";
  import "firebase/database";
  import "firebase/firestore";
  import "firebase/functions";
  import firebase from "firebase/app";
  import alerts from "../mixins/alerts";
  import { winnerRank } from "../constants/game";
  import GameSummary from "../components/GameSummary.vue";
  import NumberBoard from "../components/NumberBoard.vue";
  import ScheduleAsk from "../components/ScheduleAsk.vue";
  import CreateAgent from "../components/CreateAgent.vue";
  import AgentList from "../components/AgentList.vue";
  import AgentInfo from "../components/AgentInfo.vue";
  import GameInfo from "../components/GameInfo.vue";
  import Settings from "../components/Settings.vue";

  const SUPER_ADMINS = ["pompiborah229@gmail.com", "naamsing43@gmail.com", "ramanshekhawat75@gmail.com"];

  export default {
    name: "Admin",
    mixins: [alerts],
    components: { GameSummary, NumberBoard, ScheduleAsk, CreateAgent, AgentList, AgentInfo, GameInfo, Settings },
    data() {
      return {
        games: [],
        agents: [],
        form: {
          name: "",
          phone: "",
          ticket: ""
        },
        selectedAgent: {},
        // The game currently running, and the game whose tickets are on sale.
        liveGame: null,
        activeGame: null,
        liveData: [],
        // Claim keys enabled on the game being shown in the summary.
        gameClaims: [],
        addAgentModal: false,
        listAgentModal: false,
        agentDetailModal: false,
        gameInfoModal: false,
        settingsModal: false,
        scheduleModal: false
      };
    },
    computed: {
      ...mapState(["cards", "sheets", "info", "currentGame"]),
      isAdmin() {
        return SUPER_ADMINS.includes(this.$store.state.user.email);
      },
      titleStyle() {
        return this.info.logo_color ? { color: this.info.logo_color } : null;
      },
      showLiveBoard() {
        return !!(this.liveGame && this.liveGame.id && this.isAdmin);
      },
      lastPolled() {
        return this.liveData.length ? this.liveData[this.liveData.length - 1] : null;
      },
      ticketList() {
        return Array.isArray(this.cards) ? this.cards : [];
      },
      // The game the summary panel describes: whatever is running, else whatever
      // is on sale.
      summaryGame() {
        const game = this.liveGame || this.activeGame;
        return game ? { id: game.id, data: game.data() } : null;
      },
      soldCount() {
        return this.ticketList.filter((card) => card.name).length;
      },
      availableCount() {
        return Math.max(0, this.ticketSlots.length - this.soldCount);
      },
      sheetCount() {
        return Array.isArray(this.sheets) ? this.sheets.length : 0;
      },
      ticketCount() {
        if (!this.ticketList.length) return "0/0";
        return `${this.soldCount}/${this.ticketList.length}`;
      },
      ticketType() {
        if (!this.form.ticket) return "";
        const count = this.selectedTickets.length;
        if (count === 1) return "Single Ticket";
        if (count === 3) return "Half Sheet";
        if (count === 6) return "Full Sheet";
        return `${count} Tickets at once`;
      },
      selectedTickets() {
        return String(this.form.ticket)
          .split(",")
          .map((value) => value.trim())
          .filter(Boolean);
      },
      // One entry per ticket slot in the game, flattened so CSS Grid can lay
      // the map out at whatever column count fits the screen.
      ticketSlots() {
        if (!this.activeGame) return [];
        const limit = this.activeGame.data().limit || 0;
        // Compared numerically so a typed "012" still highlights slot 12.
        const selected = new Set(this.selectedTickets.map((ticket) => parseInt(ticket, 10)));
        const slots = [];
        for (let index = 0; index < limit; index++) {
          const number = index + 1;
          const card = this.ticketList[index];
          slots.push({
            number,
            card,
            sold: !!(card && card.name),
            selected: selected.has(number)
          });
        }
        return slots;
      }
    },
    created() {
      this.teardown = [];
      this.fetchGames();
      this.fetchAgentList();
    },
    beforeDestroy() {
      this.teardown.forEach((off) => off());
      this.teardown = [];
    },
    methods: {
      logout() {
        this.$store.dispatch("logout");
      },

      call(name, payload) {
        return firebase.functions().httpsCallable(name)(payload);
      },

      soldByAgent(email) {
        if (!email) return 0;
        const owner = email.toLowerCase();
        return this.ticketList.filter((card) => card.agent === owner).length;
      },

      statusTag(status) {
        if (status === "live") return "is-warning";
        if (status === "schedule") return "is-info";
        return "";
      },

      formatSchedule(timestamp) {
        const date = new Date(timestamp);
        return `${date.toDateString()} (${date.toLocaleTimeString()})`;
      },

      async fetchGames() {
        const gameSnap = await firebase.firestore().collection("games").orderBy("schedule", "desc").get();
        this.games = gameSnap.docs;

        this.liveGame = this.games.find((game) => game.data().status === "live");

        if (this.liveGame) {
          // Nothing is on sale while a game is running.
          this.activeGame = null;
          this.listenLiveGame();
          this.fetchGameClaims(this.liveGame.id);
          return;
        }

        this.activeGame =
          this.games.find((game) => game.data().status === "schedule") ||
          this.games.find((game) => game.data().status === "winner_declared") ||
          null;

        if (this.activeGame) {
          this.fetchGameClaims(this.activeGame.id);
        }
      },

      // The enabled claims live in the realtime database, not the Firestore doc.
      async fetchGameClaims(gameId) {
        const snap = await firebase.database().ref("games").child(gameId).child("data").child("gameType").once("value");
        this.gameClaims = Object.keys(snap.val() || {}).sort((a, b) => winnerRank(a) - winnerRank(b));
      },

      listenLiveGame() {
        const gameRef = firebase.database().ref("games").child(this.liveGame.id);

        const polledRef = gameRef.child("data").child("polled");
        const polledHandler = polledRef.on("child_added", (snap) => this.liveData.push(snap.val()));
        this.teardown.push(() => polledRef.off("child_added", polledHandler));

        const winnersRef = gameRef.child("winners");
        const winnersHandler = winnersRef.on("child_added", (snap) => {
          this.$notify({
            group: "global",
            title: "Winner!",
            text: `${snap.val().length} won ${snap.key}`
          });
        });
        this.teardown.push(() => winnersRef.off("child_added", winnersHandler));

        const unsubscribe = firebase
          .firestore()
          .collection("games")
          .doc(this.liveGame.id)
          .onSnapshot((doc) => {
            const data = doc.data();
            if (data && data.status === "winner_declared") {
              this.$buefy.dialog.alert({
                title: "Game Over!",
                ariaRole: "alertdialog",
                ariaModal: true,
                onConfirm: () => {
                  window.location.href = "/";
                }
              });
            }
          });
        this.teardown.push(unsubscribe);
      },

      async fetchAgentList() {
        const agents = await firebase.firestore().collection("agents").orderBy("name", "asc").get();
        this.agents = agents.docs;
      },

      resetForm() {
        this.form = { name: "", phone: "", ticket: "" };
      },

      assignTicket(options = {}) {
        const remove = !!options.delete;
        if ((!remove && !this.form.name) || !this.form.ticket) {
          return;
        }

        const game = this.games.find((item) => item.data().status === "schedule");
        if (!game) {
          return this.alertError({
            code: "Error",
            message: "We are not able to find any schedule game. please create one."
          });
        }

        // Agents sell against a per-game quota; admins are unlimited.
        const agent = this.agents.find((item) => item.data().email === this.$store.state.user.email);
        if (agent && !remove && this.soldByAgent(this.$store.state.user.email) >= agent.data().limit) {
          return this.alertError({
            code: "Error",
            message: "You can not assign more tickets, your limit exhausted"
          });
        }

        const tickets = this.selectedTickets.map((ticket) => parseInt(ticket, 10)).filter((ticket) => !isNaN(ticket));
        if (!tickets.length) {
          return this.alertError({ code: "Error", message: "Enter a valid ticket number." });
        }

        this.call("assign_tickets", {
          name: this.form.name,
          phone: this.form.phone,
          delete: remove,
          ticket: tickets.length > 1 ? tickets : tickets[0],
          gameId: game.id
        })
          .then((response) => {
            this.resetForm();
            this.toastSuccess(response.data.message);
          })
          .catch(this.alertError);
      },

      unAssignTicket() {
        if (this.selectedTickets.length !== 1) {
          return this.alertError({
            code: "Error",
            message: "Select exactly one ticket to unassign."
          });
        }

        const card = this.ticketList[parseInt(this.form.ticket, 10) - 1];
        if (!this.isAdmin && (!card || card.agent !== this.$store.state.user.email)) {
          return this.alertError({
            code: "Not allowed",
            message: "You can not un-assign a ticket sold by another agent."
          });
        }

        const holder = card && card.name ? `<strong>${card.name}</strong>` : "this player";
        this.confirmDanger({
          title: "Unassign ticket?",
          message: `Ticket #${card ? card.number : this.form.ticket} will be released from ${holder}. If it is part of a half or full sheet, the whole sheet may be freed too.`,
          confirmText: "Unassign ticket",
          onConfirm: () => this.assignTicket({ delete: true })
        });
      },

      createGame(data) {
        // Ticket books are generated up front, so a wrong count means starting over.
        this.$buefy.dialog.confirm({
          title: "Create this game?",
          message: `<ul>
              <li><strong>${data.limit}</strong> tickets (${data.limit / 6} full sheets)</li>
              <li>${data.per_ticket || 0} per ticket, ${data.commission || 0} commission</li>
              <li>${Object.keys(data.gameType).length} claims enabled</li>
              <li>Starts ${data.time.toLocaleString()}</li>
            </ul>`,
          confirmText: "Create game",
          cancelText: "Go back",
          type: "is-primary",
          hasIcon: true,
          icon: "calendar-today",
          onConfirm: () => this.scheduleGame(data)
        });
      },

      scheduleGame(data) {
        this.call("schedule", { ...data, time: data.time.getTime() })
          .then((response) => {
            this.toastSuccess(response.data.message);
            this.$store.dispatch("fetchStatus").then(() => {
              this.fetchGames();
              this.fetchAgentList();
            });
          })
          .catch(this.alertError);
      },

      addAgent(data) {
        this.call("add_agent", data)
          .then(() => {
            this.toastSuccess("Agent created!");
            this.fetchAgentList();
          })
          .catch(this.alertError);
      },

      deleteAgent(uid) {
        const agent = this.agents.find((item) => item.id === uid);
        const sold = agent ? this.soldByAgent(agent.data().email) : 0;

        this.confirmDanger({
          title: "Delete this agent?",
          message: agent
            ? `<strong>${agent.data().name}</strong> (${agent.data().email}) will lose access permanently.
               ${sold ? `They have sold <strong>${sold}</strong> ticket(s) in the current game.` : ""}`
            : "This agent will lose access permanently.",
          confirmText: "Delete agent",
          onConfirm: () => this.removeAgent(uid)
        });
      },

      removeAgent(uid) {
        this.call("agent_delete", { uid })
          .then(() => {
            this.toastSuccess("Agent deleted!");
            this.fetchAgentList();
            if (this.$refs.agentList) this.$refs.agentList.fetchAgentList();
          })
          .catch(this.alertError);
      },

      gameAction(game) {
        const status = game.data().status;

        if (status === "live") {
          // The most destructive action in the app: it ends a draw that players
          // are watching right now, so make it deliberate.
          return this.confirmDanger({
            title: "Stop the live game?",
            message: `This game is running and <strong>${this.liveData.length}</strong> number(s) have been called.
              Stopping it ends the draw for every player immediately and cannot be undone.`,
            confirmText: "Stop game",
            severe: true,
            requireWord: "STOP",
            onConfirm: () => this.stopGame(game.id)
          });
        }

        if (status === "winner_declared") {
          return this.$buefy.dialog.alert({
            title: "Winners",
            message: this.winnerSummary(game.data().winners),
            confirmText: "Cool!"
          });
        }

        // "schedule" and "stop" both open the game for editing.
        this.showGameInfo(game.id);
      },

      winnerSummary(winners) {
        if (!winners) return "There is no winner found!";
        return Object.keys(winners)
          .map((key) => {
            const players = winners[key]
              .map((user) => `<li>${user.name} ${user.number || ""} by agent (${user.agent})</li>`)
              .join("");
            return `<br/><h4>${key.replace(/_/g, " ").toUpperCase()}</h4><ol>${players}</ol>`;
          })
          .join("");
      },

      confirmCancelGame(gameId) {
        const game = this.games.find((item) => item.id === gameId);
        const sold = game && game.id === (this.activeGame && this.activeGame.id) ? this.soldCount : 0;

        this.confirmDanger({
          title: "Cancel this game?",
          message: `The scheduled game will be cancelled${sold ? ` and <strong>${sold}</strong> sold ticket(s) released` : ""}. This cannot be undone.`,
          confirmText: "Cancel game",
          onConfirm: () => this.stopGame(gameId)
        });
      },

      stopGame(gameId) {
        this.call("game_stop", { gameId })
          .then((response) => {
            this.fetchGames();
            this.toastSuccess(response.data.message);
          })
          .catch(this.alertError);
      },

      showTicketInfo(card) {
        if (!card) return;

        if (card.name) {
          const sheet = this.sheets.find((tickets) => tickets.includes(parseInt(card.number, 10)));
          return this.$buefy.dialog.alert({
            title: `#${card.number} ${card.name}`,
            message: `Assigned By ${card.agent} <br/> ${sheet ? "SHEET: " + sheet.join(", ") : ""}`,
            confirmText: "OK"
          });
        }

        this.selectTicket(card.number);
      },

      showAgentInfo(agent) {
        this.selectedAgent = agent.data();
        this.agentDetailModal = true;
      },

      async showGameInfo(gameId) {
        this.gameInfoModal = true;
        const info = await this.call("game_info", { gameId });
        // The modal body is only rendered once it has been opened.
        await this.$nextTick();
        if (this.$refs.selectedGame) {
          this.$refs.selectedGame.setup({ id: gameId, ...info });
        }
      },

      editGame(data) {
        this.call("game_info_update", data)
          .then((response) => {
            this.fetchGames();
            this.toastSuccess(response.data.message);
          })
          .catch(this.alertError);
      },

      changeSettings(data) {
        this.call("settings_update", data)
          .then((response) => {
            this.$store.dispatch("fetchInfo");
            this.toastSuccess(response.data.message);
          })
          .catch(this.alertError);
      },

      // Tapping a free slot in the ticket map adds it to the assignment form.
      selectTicket(number) {
        const value = String(parseInt(number, 10));
        if (this.selectedTickets.includes(value)) return;
        this.form.ticket = this.selectedTickets.concat(value).join(",");
      }
    }
  };
</script>

<style scoped>
  .admin {
    min-height: 100vh;
    padding-bottom: calc(24px + env(safe-area-inset-bottom, 0px));
    text-align: left;
  }

  .assign {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .assign__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
  }

  .assign__actions .button {
    flex: 1 1 160px;
    min-height: 44px;
  }

  .admin-row__main small {
    color: var(--tb-ink-soft);
    font-weight: 400;
  }
</style>
